// @ts-expect-error incompatibility with node16 resolution
import {Dialog, Transition} from '@headlessui/react';
import {Image, fetchSync, useLocalization} from '@shopify/hydrogen';
import type {
  Country,
  CountryCode,
} from '@shopify/hydrogen/storefront-api-types';
import clsx from 'clsx';
import {
  Suspense,
  Fragment,
  useCallback,
  useMemo,
  useState,
  useEffect,
} from 'react';
import {ChevronDownIcon} from '../icons/ChevronDown';
import SpinnerIcon from '../icons/Spinner';
import CloseIcon from '../icons/Close';
import getLocale from '../../hooks/getLocale';

type Props = {
  content?: {
    title?: object;
    text?: object;
  };
};
/**
 * A client component that selects the appropriate country to display for products on a website
 */

export default function CountrySelect({content}: Props) {
  const [open, setOpen] = useState(false);

  function setModalOpen(open: boolean) {
    setOpen(open);
    document.body.classList.toggle('country-select-open', open);
  }

  const storeSelectedLanguage = (country: string) => {
    return localStorage.setItem('language', country);
  };

  useEffect(() => {
    const currentPath = window.location.pathname;
    const alreadyChosen = localStorage.getItem('language');

    const currentPathIsHome = currentPath === '/';
    if (currentPathIsHome && alreadyChosen && alreadyChosen !== 'US') {
      window.location.href = alreadyChosen.toLowerCase() + '/';
    } else {
      setModalOpen(!alreadyChosen);
    }
  }, []);

  const {
    country: {isoCode},
  } = useLocalization();

  const currentCountry = useMemo<{name: string; isoCode: CountryCode}>(() => {
    const regionNamesInEnglish = new Intl.DisplayNames(['en'], {
      type: 'region',
    });

    return {
      name: regionNamesInEnglish.of(isoCode)!,
      isoCode: isoCode as CountryCode,
    };
  }, [isoCode]);

  const setCountry = useCallback<
    (country: Country | {name: string; isoCode: CountryCode}) => void
  >(({isoCode: newIsoCode}) => {
    storeSelectedLanguage(newIsoCode);
    redirectToNewUrl(newIsoCode.toLowerCase());
  }, []);

  const closeModal = useCallback<
    (country: Country | {name: string; isoCode: CountryCode}) => void
  >(({isoCode: newIsoCode}) => {
    storeSelectedLanguage(newIsoCode);
    setModalOpen(false);
  }, []);

  return (
    <>
      <div className="relative inline-flex">
        <button
          onClick={() => setModalOpen(true)}
          className={clsx(
            'flex h-[2rem] items-center rounded-sm bg-darkGray bg-opacity-0 p-2 font-medium',
            'hover:bg-opacity-10',
          )}
        >
          <span className="mr-2">{currentCountry.isoCode}</span>
          <ChevronDownIcon
            className={clsx(
              'h-[12px] w-[12px] transition duration-150',
              open && 'rotate-180',
            )}
          />
        </button>
      </div>
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => true}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-40 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full justify-center text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel
                  className={clsx(
                    'relative w-full max-w-[920px] transform overflow-hidden bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:mx-4 sm:rounded-md',
                    'sm:mx-12 sm:my-8 sm:w-full sm:p-6',
                  )}
                >
                  <div className="absolute right-0 top-0 pr-4 pt-4">
                    <button
                      type="button"
                      className="text-gray-400 hover:text-gray-500 focus:ring-indigo-500 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-offset-2"
                      onClick={() => closeModal(currentCountry)}
                    >
                      <span className="sr-only">Close</span>
                      <CloseIcon />
                    </button>
                  </div>
                  <Suspense
                    fallback={
                      <div className="flex h-96 items-center justify-center overflow-hidden">
                        <SpinnerIcon />
                      </div>
                    }
                  >
                    <div
                      className={'space-y-3 whitespace-pre-line py-4 sm:py-8'}
                    >
                      {content?.title && (
                        <Dialog.Title
                          as="h3"
                          className="h3 font-bold text-blue"
                        >
                          {typeof content.title === 'object'
                            ? getLocale(content.title)
                            : content.title}
                        </Dialog.Title>
                      )}
                      {content?.text && (
                        <Dialog.Description
                          className={'whitespace-pre-line text-gray'}
                        >
                          {typeof content.text === 'object'
                            ? getLocale(content.text)
                            : content.text}
                        </Dialog.Description>
                      )}
                    </div>
                    <ul
                      className={
                        'grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4'
                      }
                    >
                      <Countries
                        selectedCountry={currentCountry}
                        setCountry={setCountry}
                        closeModal={closeModal}
                      />
                    </ul>
                  </Suspense>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
}

export function Countries({
  setCountry,
  closeModal,
  selectedCountry,
}: {
  setCountry: any;
  closeModal: any;
  selectedCountry: Pick<Country, 'isoCode' | 'name'>;
}) {
  const countries: Country[] = fetchSync('/api/countries').json();
  return (
    <>
      {countries &&
        countries.map((country) => {
          const isSelected = country.isoCode === selectedCountry?.isoCode;
          return (
            <li key={country.isoCode}>
              <button
                onClick={() =>
                  isSelected ? closeModal(country) : setCountry(country)
                }
                className={clsx(
                  !isSelected && 'border-lightGray',
                  'flex w-full space-x-3 rounded-sm border p-3 hover:bg-[#F0F0F0]',
                )}
              >
                <div
                  className={clsx(
                    'h-[21px] w-[21px] overflow-hidden rounded-full border border-lightGray',
                  )}
                >
                  <Image
                    src={'/flags/' + country.isoCode.toLowerCase() + '.svg'}
                    className={'h-full w-full'}
                    alt={'Flag ' + country.isoCode}
                    height={21}
                    width={21}
                    aria-hidden={true}
                  />
                </div>

                <div className="mr-8 flex items-center space-x-2">
                  <span>{country.name}</span>
                </div>
              </button>
            </li>
          );
        })}
    </>
  );
}

export function redirectToNewUrl(locale: string) {
  let lastUrlSegment = window.location.pathname.split('/').pop();
  lastUrlSegment = lastUrlSegment ? lastUrlSegment : 'home';
  const url = `/api/redirect/${locale}/${lastUrlSegment}`;

  try {
    fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    }).then((res) => {
      if (res.ok) {
        res.text().then((response) => {
          return (window.location.href = response);
        });
      }
    });
  } catch (error) {
    return {
      error:
        (error instanceof Error)?.toString() || 'An unknown error has occurred',
    };
  }
}
